import React, { useState, useMemo, useRef } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { getBaseRelationshipType, getRelationshipBasedOnSex } from '../utils/relationshipMappings';

const { FiX, FiPrinter, FiUser, FiChevronDown, FiHeart } = FiIcons;

const getGenerationOffset = (relType) => {
  const type = (relType || '').toLowerCase();
  const base = getBaseRelationshipType(type);

  if (['parent', 'father', 'mother', 'step_parent', 'parent_in_law'].includes(base)) return -1;
  if (['child', 'son', 'daughter', 'step_child', 'child_in_law'].includes(base)) return 1;
  if (base === 'grandparent') return -2;
  if (base === 'great_grandparent') return -3;
  if (base === 'grandchild') return 2;
  if (base === 'great_grandchild') return 3;
  if (base === 'parental_sibling' || base === 'parental_sibling_in_law') return -1;
  if (base === 'sibling_child' || base === 'sibling_child_in_law') return 1;
  if (base === 'grandparent_sibling') return -2;
  if (base === 'grandsibling_child') return 1;
  if (['spouse', 'sibling', 'half_sibling', 'step_sibling', 'sibling_in_law',
       'cousin', 'cousin_in_law', 'cousin_once_removed', 'ex_spouse'].includes(base)) return 0;
  return 0;
};

const generationLabels = {
  '-3': 'Great-Grandparents',
  '-2': 'Grandparents',
  '-1': 'Parents & Aunts/Uncles',
  '0': 'Self & Siblings',
  '1': 'Children & Nieces/Nephews',
  '2': 'Grandchildren',
  '3': 'Great-Grandchildren',
};

const buildFamilyTree = (selectedPersonId, people, relationships) => {
  const generations = {};
  const personRelLabels = {};
  const visited = new Set();
  const queue = [{ id: String(selectedPersonId), generation: 0, label: 'Self' }];
  visited.add(String(selectedPersonId));
  personRelLabels[String(selectedPersonId)] = 'Self';

  while (queue.length > 0) {
    const { id, generation } = queue.shift();

    if (!generations[generation]) generations[generation] = [];
    const alreadyAdded = generations[generation].some(p => String(p.id) === String(id));
    if (!alreadyAdded) {
      const person = people.find(p => String(p.id) === String(id));
      if (person) generations[generation].push(person);
    }

    const rels = relationships.filter(r =>
      String(r.person_a_id) === String(id) || String(r.person_b_id) === String(id)
    );

    for (const rel of rels) {
      const isA = String(rel.person_a_id) === String(id);
      const otherId = String(isA ? rel.person_b_id : rel.person_a_id);
      const relType = isA ? rel.relationship_type_b : rel.relationship_type;

      if (visited.has(otherId)) continue;

      const offset = getGenerationOffset(relType);
      const otherGeneration = generation + offset;
      const baseType = getBaseRelationshipType(relType);
      const otherPerson = people.find(p => String(p.id) === otherId);
      const displayLabel = otherPerson ? getRelationshipBasedOnSex(baseType, otherPerson.sex) : relType;

      visited.add(otherId);
      personRelLabels[otherId] = displayLabel;
      queue.push({ id: otherId, generation: otherGeneration, label: displayLabel });
    }
  }

  return { generations, personRelLabels };
};

const PersonNode = ({ person, label, isCenter }) => {
  const sexColor = person.sex === 'male' ? 'border-blue-400 bg-blue-50' :
                   person.sex === 'female' ? 'border-pink-400 bg-pink-50' :
                   'border-purple-400 bg-purple-50';
  const textColor = person.sex === 'male' ? 'text-blue-700' :
                    person.sex === 'female' ? 'text-pink-700' :
                    'text-purple-700';

  return (
    <div className={`print-node flex flex-col items-center mx-2 my-1 ${isCenter ? 'print-center-node' : ''}`}>
      <div className={`w-16 h-16 md:w-20 md:h-20 rounded-full border-3 ${sexColor} overflow-hidden flex items-center justify-center shadow-sm print:shadow-none`}>
        {person.photo_url ? (
          <img src={person.photo_url} alt={person.name} className="w-full h-full object-cover print:grayscale" />
        ) : (
          <SafeIcon icon={FiUser} className={`${textColor} text-2xl`} />
        )}
      </div>
      <p className={`mt-1 text-xs md:text-sm font-bold ${textColor} text-center leading-tight max-w-[80px] md:max-w-[100px] truncate print:text-black`}>
        {person.name}
      </p>
      <span className="text-[9px] md:text-[10px] text-gray-500 font-medium capitalize print:text-gray-600">
        {label}
      </span>
    </div>
  );
};

const CoupleUnit = ({ personA, personB, labelA, labelB, isCenter }) => (
  <div className="flex items-center mx-3 my-1">
    <PersonNode person={personA} label={labelA} isCenter={isCenter} />
    <div className="flex flex-col items-center mx-1 print-couple-link">
      <SafeIcon icon={FiHeart} className="text-red-400 text-xs print:text-gray-400" />
    </div>
    <PersonNode person={personB} label={labelB} />
  </div>
);

const PrintableFamilyTree = ({ people, relationships, onClose }) => {
  const [selectedPersonId, setSelectedPersonId] = useState('');
  const [isPrinting, setIsPrinting] = useState(false);
  const printRef = useRef(null);

  const sortedPeople = useMemo(() =>
    [...people].sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    [people]
  );

  const { generations, personRelLabels } = useMemo(() => {
    if (!selectedPersonId) return { generations: {}, personRelLabels: {} };
    return buildFamilyTree(selectedPersonId, people, relationships);
  }, [selectedPersonId, people, relationships]);

  const sortedGenerations = useMemo(() => {
    return Object.entries(generations)
      .sort(([a], [b]) => Number(a) - Number(b))
      .map(([gen, members]) => ({ generation: Number(gen), members }));
  }, [generations]);

  const groupCouples = (members) => {
    const couples = [];
    const singles = [];
    const used = new Set();

    for (const person of members) {
      if (used.has(String(person.id))) continue;

      const spouseRel = relationships.find(r => {
        const isA = String(r.person_a_id) === String(person.id);
        const otherId = String(isA ? r.person_b_id : r.person_a_id);
        const relType = isA ? r.relationship_type_b : r.relationship_type;
        const base = getBaseRelationshipType(relType);
        const isSpouse = ['spouse', 'ex_spouse'].includes(base);
        const otherInSameGen = members.some(p => String(p.id) === otherId);
        return isSpouse && otherInSameGen && !used.has(otherId);
      });

      if (spouseRel) {
        const spouseId = String(spouseRel.person_a_id) === String(person.id)
          ? spouseRel.person_b_id : spouseRel.person_a_id;
        const spouse = members.find(p => String(p.id) === String(spouseId));
        if (spouse) {
          couples.push({ personA: person, personB: spouse });
          used.add(String(person.id));
          used.add(String(spouse.id));
        }
      } else {
        singles.push(person);
        used.add(String(person.id));
      }
    }

    return { couples, singles };
  };

  const buildPrintHtml = () => {
    if (!printRef.current) return '';
    const content = printRef.current.innerHTML;
    const scriptOpen = '<' + 'script>';
    const scriptClose = '<' + '/script>';
    const tailwindOpen = '<' + 'script src="https://cdn.tailwindcss.com"><' + '/script>';

    return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Family Tree - ${selectedPerson?.name || ''}</title>
  ${tailwindOpen}
  <style>
    * { box-sizing: border-box; }
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; margin: 0; padding: 20px; background: #fff; }
    .print-node img { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    .print-center-node > div:first-child {
      border-width: 4px !important;
      box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.3) !important;
    }
    .print-gen-row {
      page-break-inside: avoid;
      border-bottom: 1px dashed #e5e7eb;
      padding-bottom: 12px;
      margin-bottom: 12px;
    }
    .print-couple-link { margin: 0 4px; }
    @media print {
      body { padding: 0; }
      @page { margin: 1cm; size: landscape; }
    }
    @media screen {
      body { max-width: 1100px; margin: 0 auto; }
    }
  </style>
</head>
<body>
  ${content}
  ${scriptOpen}
    window.onload = function() { setTimeout(function() { window.print(); }, 600); };
  ${scriptClose}
</body>
</html>`;
  };

  const handlePrint = () => {
    setIsPrinting(true);
    const html = buildPrintHtml();
    const printWindow = window.open('', '_blank', 'width=1100,height=800');
    if (printWindow) {
      printWindow.document.open();
      printWindow.document.write(html);
      printWindow.document.close();
    } else {
      alert('Please allow pop-ups to print the family tree.');
    }
    setTimeout(() => setIsPrinting(false), 1000);
  };

  const selectedPerson = people.find(p => String(p.id) === String(selectedPersonId));
  const today = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

  return (
    <motion.div
      key="print-tree-modal"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        className="bg-white rounded-3xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
              <SafeIcon icon={FiPrinter} className="text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-800">Print Family Tree</h2>
              <p className="text-xs text-gray-500">Select a person to generate their family tree</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-xl transition-colors">
            <SafeIcon icon={FiX} className="text-gray-500" />
          </button>
        </div>

        {/* Person Selector */}
        <div className="px-6 py-4 bg-gray-50 border-b border-gray-100">
          <div className="relative max-w-md">
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 block">
              Select Person
            </label>
            <div className="relative">
              <select
                value={selectedPersonId}
                onChange={(e) => setSelectedPersonId(e.target.value)}
                className="w-full appearance-none bg-white border-2 border-gray-200 rounded-xl px-4 py-3 pr-10 text-sm font-medium text-gray-700 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-all"
              >
                <option value="">Choose a person...</option>
                {sortedPeople.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
              <SafeIcon icon={FiChevronDown} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            </div>
          </div>
        </div>

        {/* Preview Area */}
        <div className="flex-1 overflow-auto p-6 bg-gray-50">
          {!selectedPersonId ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-400">
              <SafeIcon icon={FiUser} className="text-6xl mb-4 opacity-30" />
              <p className="text-lg font-medium">Select a person to preview their family tree</p>
              <p className="text-sm mt-1">The tree will show all connected family members</p>
            </div>
          ) : sortedGenerations.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-400">
              <SafeIcon icon={FiUser} className="text-6xl mb-4 opacity-30" />
              <p className="text-lg font-medium">No family connections found</p>
              <p className="text-sm mt-1">Add relationships to see the family tree</p>
            </div>
          ) : (
            <div ref={printRef} id="print-area" className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 md:p-8">
              {/* Print Header */}
              <div className="text-center mb-8">
                <h1 className="text-2xl md:text-3xl font-bold text-gray-800">
                  {selectedPerson?.name}'s Family Tree
                </h1>
                <p className="text-sm text-gray-500 mt-1">
                  Generated on {today} • {Object.values(generations).flat().length} family members
                </p>
                <div className="w-24 h-1 bg-gradient-to-r from-blue-500 to-purple-500 mx-auto mt-3 rounded-full" />
              </div>

              {/* Legend */}
              <div className="flex justify-center gap-6 mb-6">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded-full border-2 border-blue-400 bg-blue-50" />
                  <span className="text-xs text-gray-600">Male</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded-full border-2 border-pink-400 bg-pink-50" />
                  <span className="text-xs text-gray-600">Female</span>
                </div>
                <div className="flex items-center gap-2">
                  <SafeIcon icon={FiHeart} className="text-red-400 text-xs" />
                  <span className="text-xs text-gray-600">Married/Partnered</span>
                </div>
              </div>

              {/* Generations */}
              {sortedGenerations.map(({ generation, members }) => {
                const { couples, singles } = groupCouples(members);
                return (
                  <div key={generation} className="print-gen-row mb-6 last:mb-0">
                    <div className="flex items-center mb-3">
                      <div className="h-px flex-1 bg-gray-200" />
                      <span className="px-3 text-xs font-bold text-gray-500 uppercase tracking-wider">
                        {generationLabels[String(generation)] || `Generation ${generation > 0 ? '+' : ''}${generation}`}
                      </span>
                      <div className="h-px flex-1 bg-gray-200" />
                    </div>

                    <div className="flex flex-wrap justify-center items-start gap-2">
                      {couples.map(({ personA, personB }) => (
                        <CoupleUnit
                          key={`couple-${personA.id}-${personB.id}`}
                          personA={personA}
                          personB={personB}
                          labelA={personRelLabels[String(personA.id)] || ''}
                          labelB={personRelLabels[String(personB.id)] || ''}
                          isCenter={String(personA.id) === String(selectedPersonId) || String(personB.id) === String(selectedPersonId)}
                        />
                      ))}
                      {singles.map(person => (
                        <PersonNode
                          key={`single-${person.id}`}
                          person={person}
                          label={personRelLabels[String(person.id)] || ''}
                          isCenter={String(person.id) === String(selectedPersonId)}
                        />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer Actions */}
        {selectedPersonId && sortedGenerations.length > 0 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 bg-white">
            <p className="text-xs text-gray-500">
              {Object.values(generations).flat().length} family members across {sortedGenerations.length} generations
            </p>
            <div className="flex items-center space-x-3">
              <button
                onClick={onClose}
                className="px-5 py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-xl transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handlePrint}
                disabled={isPrinting}
                className="px-6 py-2.5 bg-gradient-to-r from-blue-600 to-purple-600 text-white text-sm font-bold rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all shadow-lg flex items-center space-x-2 disabled:opacity-50"
              >
                <SafeIcon icon={FiPrinter} />
                <span>Print Tree</span>
              </button>
            </div>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
};

export default PrintableFamilyTree;